import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useEditHubModalStore } from './EditHubModalStore';

import { UpdateHub } from 'api/traps/types';
import useGetHub from 'hooks/Traps/Hubs/useGetHub';
import useUpdateHub from 'hooks/Traps/Hubs/useUpdateHub';
import { MLInput } from 'components/Input/Input';
import { MLButton } from 'components/Button/Button';

interface ManageHubModalProps {
  refetchHubs: () => void;
}

const EditHubModal: React.FC<ManageHubModalProps> = ({ refetchHubs }) => {
  const { isOpen, onEditHubModalClose, hub } = useEditHubModalStore();
  // useUpdateHub
  const { hubInfo } = useGetHub(hub?.pk || 0);
  const { updateHub } = useUpdateHub();
  const { t } = useTranslation();

  const used_sequence_numbers = useMemo(() => {
    // Повертає масив використаних порядкових номерів.
    return hubInfo?.used_sequences || [];
  }, [hubInfo]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<UpdateHub>({
    defaultValues: {
      sequence_number: 0,
    },
  });

  useEffect(() => {
    // Підгружає порядковий номер в модалку.
    setValue('sequence_number', hubInfo?.current_sn || 0);
  }, [hubInfo, setValue]);

  const onReset = () => {
    reset();
    onEditHubModalClose();
  };

  const onHubEdit = (data: UpdateHub) => {
    updateHub(
      {
        hubId: hub?.pk || 0,
        hubInfo: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Hub updated successfully', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchHubs();
        },
        onError: () => {
          enqueueSnackbar('Hub did`t update', { variant: 'error' });
          onReset();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onReset} size="md">
      <ModalHeader toggle={onReset} className=" p-3 bg-soft-info">
        {t('TrapModals.manageModal.editTrap')}
        {` ${hub?.serialNumber}`}
      </ModalHeader>
      <ModalBody>
        <div className="row">
          <div className="mb-3 col-md-12">
            <Controller
              control={control}
              name="sequence_number"
              rules={{
                required: 'Required field',
                min: { value: 1, message: 'Sequence number must be positive' },
              }}
              render={({ field, formState: { errors } }) => (
                <MLInput
                  {...field}
                  label={t('TrapModals.manageModal.sequenceNumber')}
                  error={errors?.sequence_number?.message}
                  required
                  type="number"
                  onChange={e => {
                    const value = +e.target.value;
                    field.onChange(value);
                    if (!used_sequence_numbers.includes(value)) {
                      clearErrors('sequence_number');
                    }
                  }}
                  onBlur={() => {
                    const value = +field.value;
                    if (used_sequence_numbers.includes(value)) {
                      setError('sequence_number', {
                        type: 'manual',
                        message: t('TrapModals.manageModal.InUse'),
                      });
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="w-100 d-flex justify-content-end">
          <MLButton
            onClick={handleSubmit(data => {
              if (Object.keys(errors).length === 0) {
                onHubEdit(data);
              } else {
                enqueueSnackbar('fix erors', { variant: 'error' });
              }
            })}
            isGhost
            color="success"
            innerText={t('TrapModals.confirm')}
            disabled={Object.keys(errors).length > 0}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default EditHubModal;
